import { MarketingCta, MarketingLayout } from "@/components/marketing/MarketingLayout";

const VALUES = [
  {
    num: "No commission",
    title: "Ever, on anything",
    body: "One flat monthly price, however well your business does. What your customers pay is what reaches you.",
  },
  {
    num: "Nothing to learn",
    title: "We handle the technical side",
    body: "You bring your business — name, menu, photos. We bring the setup, the AI help, and the ongoing upkeep.",
  },
  {
    num: "Built alongside you",
    title: "Not built and abandoned",
    body: "As tools and customer expectations change, BuildHof keeps changing with them — not a one-time project.",
  },
];

export function MarketingAboutPage() {
  return (
    <MarketingLayout activePath="/about">
      <section className="about-hero" data-testid="public-about-content">
        <div className="wrap">
          <div className="about-hero-grid">
            <div>
              <span className="eyebrow">Our story</span>
              <h1 className="headline" data-testid="public-about-title">
                We started BuildHof because people were walking past a business <em>they never knew was there.</em>
              </h1>
              <p style={{ fontSize: 16, color: "var(--text-secondary)", lineHeight: 1.7, maxWidth: 480 }} data-testid="public-about-body">
                A neighborhood shop, a warm owner, everything in place to be a gathering spot — except from the street, you&apos;d never know it existed. That&apos;s the moment this actually started.
              </p>
            </div>
            <div>
              <img
                src="/marketing/about-hero.jpg"
                alt="A neighborhood storefront, the kind of shop BuildHof was built for"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="story" data-testid="about-story">
        <div className="wrap">
          <div className="story-block">
            <p className="lede">This is the story of a company that started with something we were seeing every day, not far from home.</p>
            <p>We live in a neighborhood full of boutique shops and a community that genuinely knows itself — the kind of place where people still recognize each other by name. That&apos;s rare, and it felt worth protecting.</p>
            <p>The work didn&apos;t really begin, though, until a friend brought us into a small local store one afternoon. The owner was warm, generous with their time, and had built a place that should have been a natural gathering spot for the neighborhood. But from the street, you&apos;d never know it was there. People were walking right past a business that had everything to offer them, simply because they didn&apos;t know it existed.</p>
          </div>
          <div className="pull">That&apos;s the moment this platform actually started — not as an idea on paper, but as a real problem we wanted to help solve.</div>
          <div className="story-block">
            <p>The goal was never to change what makes these businesses special. It&apos;s to help them hold onto that, while opening a small door online — enough that the people already nearby, who just haven&apos;t found them yet, finally can.</p>
            <p>Getting online has traditionally meant one of two paths: hire an agency, at a cost that&apos;s hard to justify before you&apos;ve seen any return, or take it on yourself, learning tools and terms that have nothing to do with running your actual business. Neither path was really built with a small, busy owner in mind — so we built a third one.</p>
            <p>Everything technical is handled for you. You bring your business — its name, your menu or products, a few photos if you have them — and we bring the rest, including AI help with writing and images if you want it. One plan, with everything a business needs to get started, so there&apos;s nothing to figure out before you begin.</p>
            <p>We don&apos;t take a cut of your sales — not ever, no matter how well you do. Just one flat monthly fee, so getting started never means a big upfront risk. We ask for a three-month commitment at the start, because that&apos;s genuinely how long it takes to see what being online can do for a business — after that, you&apos;re free to stay or go, month to month, no questions asked.</p>
            <p>And we&apos;re not just building this and walking away. As things change — new tools, new expectations from customers — we&apos;ll keep building alongside you.</p>
          </div>
        </div>
      </section>

      <section className="values" data-testid="about-values">
        <div className="wrap">
          <div className="section-heading" style={{ marginLeft: "auto", marginRight: "auto", textAlign: "center", maxWidth: 560 }}>
            <p className="kicker">What that means in practice</p>
            <h2>The principles behind BuildHof.</h2>
          </div>
          <div className="values-grid">
            {VALUES.map((item) => (
              <div className="value-card" key={item.title}>
                <p className="num">{item.num}</p>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="team-note" data-testid="about-team">
        <div className="wrap">
          <p>
            BuildHof is part of <strong>Build Up Rise Up UG</strong>, based in Munich. The same small team behind Build &amp; Hire and Marathi Referrals builds and supports BuildHof — there&apos;s no separate team to introduce, just the same people, one more way we&apos;re trying to help.
          </p>
        </div>
      </section>

      <MarketingCta
        title="Ready to open that door online?"
        body="Under an hour, once your menu and photos are ready."
      />
    </MarketingLayout>
  );
}
